<template>
  <section
    id="video"
    ref="revealTarget"
    class="video-section home-scroll-reveal"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <div class="video-background" aria-hidden="true">
      <img src="/images/home/video-center-cover.png" alt="" loading="lazy" />
    </div>
    <div class="video-tint" aria-hidden="true"></div>

    <div class="page-container video-content">
      <div class="video-copy">
        <span class="section-kicker">VIDEO CENTER</span>
        <h2>从科研现场，看见产业发生</h2>
        <p>通过企业形象、人物访谈和专题视频，了解兴科的科研实践、产业服务与团队行动。</p>
        <nuxt-link v-if="videoPath" class="video-more" :to="videoPath">
          查看更多视频
          <i class="i-custom-enter more-link-icon-desktop" aria-hidden="true"></i>
          <i class="i-lucide-chevron-right more-link-icon-mobile" aria-hidden="true"></i>
        </nuxt-link>
      </div>

      <button
        ref="playTrigger"
        class="video-play"
        type="button"
        aria-haspopup="dialog"
        :aria-label="`播放视频：${video?.title || '兴科视频'}`"
        :disabled="!hasPlayableVideo"
        @click="openPlayer"
      >
        <i class="i-lucide-play" aria-hidden="true"></i>
      </button>
      <p v-if="!hasPlayableVideo" class="video-status" role="status">视频内容正在整理中</p>
    </div>
  </section>

  <media-player-dialog
    :media="selectedMedia"
    @close="selectedMedia = undefined"
    @closed="restorePlayerFocus"
  />
</template>

<script setup lang="ts">
  import type { MediaItem } from '~/types/site-api'

  import { findMediaCategory, getMediaCategoryEntryRoute } from '~/utils/content'

  defineOptions({
    name: 'HomeVideo',
  })

  const props = withDefaults(
    defineProps<{
      video?: MediaItem | null
    }>(),
    {
      video: null,
    }
  )

  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.12)

  const playTrigger = useTemplateRef<HTMLButtonElement>('playTrigger')
  const selectedMedia = shallowRef<MediaItem>()
  const hasPlayableVideo = computed(() => Boolean(props.video?.playback_url))
  const { categoryTree } = useMediaCategories()
  const videoPath = computed(() => {
    const code = props.video?.category?.code
    const match = code ? findMediaCategory(categoryTree.value, code) : undefined
    return match ? getMediaCategoryEntryRoute(match.root).path : undefined
  })

  /** 以弹窗播放视频，避免外部视频比例与首页色带版式互相牵制。 */
  function openPlayer() {
    if (!hasPlayableVideo.value || !props.video) return
    selectedMedia.value = props.video
  }

  /** 弹窗关闭后把焦点还给播放按钮，保持键盘导航连续。 */
  function restorePlayerFocus() {
    if (playTrigger.value?.isConnected) playTrigger.value.focus()
  }
</script>

<style scoped lang="scss">
  .video-section {
    position: relative;
    height: 505px;
    overflow: hidden;
    color: var(--white);
    background: #6f272e;
    isolation: isolate;
  }

  .video-background,
  .video-tint {
    position: absolute;
    inset: 0;
  }

  .video-background {
    z-index: 0;
    overflow: hidden;
  }

  .video-background img {
    display: block;
    width: 100%;
    height: 130%;
    object-fit: cover;
    object-position: center center;
  }

  .video-tint {
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgb(120 21 30 / 72%) 0%,
      rgb(163 52 60 / 58%) 52%,
      rgb(154 54 61 / 46%) 100%
    );
  }

  .video-content {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    height: 505px;
  }

  .video-copy {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    width: min(440px, 100%);
  }

  .section-kicker {
    display: inline-flex;
    gap: 10px;
    align-items: center;
    font:
      650 12px/1.4 ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace;
    color: #fff;
  }

  .section-kicker::before {
    width: 28px;
    height: 2px;
    content: '';
    background: currentColor;
  }

  .video-copy h2 {
    max-width: 430px;
    margin: 0;
    font-size: clamp(32px, 3.2vw, 46px);
    font-weight: 620;
    color: #fff;
  }

  .video-copy p {
    max-width: 32em;
    color: rgb(255 255 255 / 88%);
  }

  .video-more {
    display: inline-flex;
    gap: 12px;
    align-items: center;
    justify-content: center;
    font-weight: 520;
    line-height: 1.5;
    color: #fff;
    cursor: pointer;
    border: 1px solid rgb(255 255 255 / 88%);
    border-radius: 999px;
  }

  .video-more i {
    width: 16px;
    height: 16px;
    transition: transform var(--fast) var(--ease);
  }

  .video-more:hover i {
    transform: translateX(4px);
  }

  .video-play {
    position: absolute;
    top: 50%;
    left: 50%;
    display: grid;
    place-items: center;
    width: 108px;
    height: 108px;
    padding: 0;
    color: #fff;
    cursor: pointer;
    background: rgb(255 255 255 / 6%);
    border: 2px solid rgb(255 255 255 / 82%);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition:
      background var(--fast) var(--ease),
      transform var(--fast) var(--ease);
  }

  .video-play:hover {
    background: rgb(255 255 255 / 14%);
    transform: translate(-50%, -50%) scale(1.06);
  }

  .video-play:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }

  .video-play:disabled:hover {
    background: rgb(255 255 255 / 6%);
    transform: translate(-50%, -50%);
  }

  .video-play i {
    width: 38px;
    height: 38px;
    margin-left: 4px;
  }

  .video-status {
    position: absolute;
    top: calc(50% + 72px);
    left: 50%;
    width: min(320px, calc(100% - 48px));
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: rgb(255 255 255 / 90%);
    text-align: center;
    transform: translateX(-50%);
  }

  @media (min-width: 768px) {
    .video-copy {
      height: 393px;
    }

    .section-kicker {
      margin-bottom: 24px;
    }

    .video-copy h2 {
      line-height: 1.35;
    }

    .video-copy p {
      padding: 20px 0 26px;
      font-size: 16px;
      line-height: 1.8;
    }

    .video-more {
      min-width: 210px;
      padding: 16px 26px;
      margin-top: 56px;
      font-size: 16px;
    }
  }

  @media (max-width: 767px) {
    .video-section,
    .video-content {
      height: auto;
      min-height: 540px;
    }

    .video-background {
      bottom: 0;
      height: 100%;
    }

    .video-background img {
      object-position: 56% center;
    }

    .video-tint {
      background: linear-gradient(
        180deg,
        rgb(111 20 29 / 74%) 0%,
        rgb(153 42 51 / 64%) 58%,
        rgb(113 20 29 / 80%) 100%
      );
    }

    .video-content {
      align-items: stretch;
      padding-block: 40px 32px;
    }

    .video-copy {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      column-gap: 16px;
      place-content: start stretch;
      width: 100%;
      height: auto;
      min-height: 0;
    }

    .section-kicker {
      grid-row: 1;
      grid-column: 1;
      align-self: center;
      min-width: 0;
      margin-bottom: 0;
    }

    .video-copy h2 {
      grid-row: 2;
      grid-column: 1 / -1;
      max-width: 10.5em;
      margin-top: 18px;
      font-size: 32px;
      line-height: 1.28;
    }

    .video-copy p {
      grid-row: 3;
      grid-column: 1 / -1;
      max-width: 28em;
      padding: 0;
      margin: 16px 0 0;
      font-size: 14px;
      line-height: 1.75;
    }

    .video-more {
      grid-row: 1;
      grid-column: 2;
      gap: 8px;
      place-self: center end;
      min-width: 0;
      min-height: 44px;
      padding: 0;
      margin: 0;
      font-size: 14px;
      font-weight: 400;
      white-space: nowrap;
      background: transparent;
      border: 0;
      border-radius: 0;
    }

    .video-play {
      inset: 68% auto auto 50%;
      width: 72px;
      height: 72px;
      transform: translate(-50%, -100%);
    }

    .video-play:hover {
      transform: translate(-50%, -100%) scale(1.05);
    }

    .video-play:disabled:hover {
      transform: translate(-50%, -100%);
    }

    .video-play i {
      width: 28px;
      height: 28px;
    }

    .video-status {
      inset: calc(68% + 52px) auto auto 50%;
      width: min(250px, calc(100% - 48px));
      font-size: 12px;
      text-align: center;
      transform: translateX(-50%);
    }
  }
</style>
